/**
 * Kontantgaver — cash handed to the mosque, recorded by one staff member and confirmed by another, with a numbered
 * receipt.  MKK module 22 «Kontantgaver» (R2, owner 2026-10-29). Staff only; nothing here is public or in the member app.
 *
 * Behind the `cash_gifts` capability (the menu item is absent without it, and every route answers 404).
 *   cash_gifts.record    register a gift, take back one's own before it is confirmed, register a correction;
 *   cash_gifts.confirm   confirm or reject somebody else's entry — never one's own (four eyes);
 *   cash_gifts.lock      lock a month: nothing dated in it is registered or confirmed afterwards.
 * The server says what this person may do with each entry (`can`) and decides again on each request.
 *
 * A list never shows the giver; the giver's name and contact are shown only when one entry is opened. Confirm, reject,
 * correct and lock take two clicks (TwoStep). The receipt and the CSV are fetched with the session (audited downloads);
 * the CSV with the giver's name is offered only to cash_gifts.lock holders (the server refuses it to others).
 *
 * «Bekreftet med Vipps» (owner 2026-10-29, «connect high items only»; components/VippsConfirm): the second pair of eyes may
 * press «Bekreft gaven med Vipps» — when Vipps comes back, that confirms the entry (and numbers the receipt) in the same
 * step; a lock holder may press «Lås måneden og bekreft med Vipps». With the mosque's «Krev bekreftelse med Vipps på
 * kontantgaver» on, the plain «Bekreft» and «Lås» are not offered (the server says so in `can`). Never «signatur».
 */
import React, { useState } from 'react';
import { can, download, errSummary, errText, fieldErrors, post, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { longDate, osloTime, isoToday } from '../lib/format';
import { Body as CardBody, Btn, Card, Field, FieldError, LoadError, Loading, Msg, Tag, TwoStep, type TagTone } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { Stamp, VoidedStamps, startConfirm, useVippsReturn } from '../components/VippsConfirm';
import { Campaigns, CashGifts, Expenses } from '../core';
import { PersonSearch } from '../components/people';
import { personHref } from './Register';

const PURPOSE = CashGifts.PURPOSE_NO;
const STATE_TONE: Record<string, TagTone> = { recorded: 'gold', confirmed: 'green', rejected: 'grey' };
export const stateTag = (s: string) => <Tag tone={STATE_TONE[s] || 'grey'}>{CashGifts.STATE_NO[s] || s}</Tag>;
const krText = (ore: number) => Expenses.kr(ore);

// memberId / memberLabel (members register «pro», 2026-10-31): the person in the register, picked by those who may search it.
const EMPTY = { amount: '', receivedOn: '', purpose: 'general', campaignId: '', donorName: '', donorContact: '', note: '', memberId: '', memberLabel: '' };
type Form = typeof EMPTY;
/** A campaign as the picker offers it (MKK round 2 integration: a confirmed cash gift counts towards its bar). */
type CampaignOption = { id: string; title: string; phase: string; purpose: string };

/**
 * The campaigns a cash gift may be tied to: every ACTIVE (not draft, not closed) campaign, running ones first, read from
 * /campaigns when this person may read them. null while unknown or when campaigns are off / not readable: the form then
 * keeps the plain id field it had.
 */
function useCampaignOptions(enabled: boolean): CampaignOption[] | null {
  const mayRead = enabled && (can('campaigns.read') || can('campaigns.manage'));
  const { data, error } = useLoad<Body>(mayRead ? '/campaigns' : null, [mayRead]);
  if (!mayRead || error || !data || !Array.isArray(data.items)) return null;
  return (data.items as Body[]).filter((c) => c.status === 'active')
    .map((c) => ({ id: String(c.id), title: String(c.title), phase: String(c.phase || ''), purpose: String(c.donationPurpose || '') }))
    .sort((a, b) => Number(b.phase === 'running') - Number(a.phase === 'running'));
}

function GiftForm({ f, set, errors, today, campaigns }: { f: Form; set: (f: Form) => void; errors: Record<string, string>; today: string; campaigns: CampaignOption[] | null }) {
  // FieldError: red under the field and a toast (2026-10-31).
  const err = (k: string) => <FieldError text={errors[k]} />;
  // A gift to a campaign is given under the campaign's purpose (the server sets it, as for a Vipps gift).
  const chosen = (campaigns && f.campaignId && campaigns.find((c) => c.id === f.campaignId)) || null;
  const pickCampaign = (id: string) => {
    const c = campaigns && campaigns.find((x) => x.id === id);
    set({ ...f, campaignId: id, ...(c && CashGifts.PURPOSES.includes(c.purpose) ? { purpose: c.purpose } : {}) });
  };
  return (
    <>
      <div className="row">
        <Field label="Beløp (kr)" required className="field half" note="For eksempel 500 eller 1 250,50.">{(id, d) => <><input id={id} aria-describedby={d} inputMode="decimal"
          autoComplete="off" value={f.amount} onChange={(e) => set({ ...f, amount: e.target.value })} />{err('amountOre')}</>}</Field>
        <Field label="Dato mottatt" required className="field half">{(id) => <><input id={id} type="date" max={today} value={f.receivedOn}
          onChange={(e) => set({ ...f, receivedOn: e.target.value })} />{err('receivedOn')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Formål" required className="field half" note={chosen ? 'Innsamlingens formål.' : undefined}>{(id, d) => <><select id={id} aria-describedby={d}
          disabled={Boolean(chosen)} value={f.purpose} onChange={(e) => set({ ...f, purpose: e.target.value })}>
          {CashGifts.PURPOSES.map((p) => <option key={p} value={p}>{PURPOSE[p]}</option>)}</select>{err('purpose')}</>}</Field>
        {campaigns ? (
          <Field label="Innsamling (valgfritt)" className="field half" note="Bekreftede kontantgaver teller med i innsamlingens fremdrift. Datoen må være innenfor innsamlingens dager.">{(id, d) => <><select id={id}
            aria-describedby={d} value={f.campaignId} onChange={(e) => pickCampaign(e.target.value)}>
            <option value="">Ingen innsamling</option>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.title}{c.phase && c.phase !== 'running' ? ` (${Campaigns.PHASE_NO[c.phase] || c.phase})` : ''}</option>)}
            {f.campaignId && !campaigns.some((c) => c.id === f.campaignId) && <option value={f.campaignId}>{f.campaignId}</option>}
          </select>{err('campaignId')}</>}</Field>
        ) : (
          <Field label="Kampanje (valgfritt)" className="field half" note="Kampanjens id, hvis gaven gjelder en innsamling.">{(id, d) => <><input id={id} aria-describedby={d}
            maxLength={CashGifts.MAX_CAMPAIGN} autoComplete="off" value={f.campaignId} onChange={(e) => set({ ...f, campaignId: e.target.value })} />{err('campaignId')}</>}</Field>
        )}
      </div>
      <div className="row">
        <Field label="Giverens navn (valgfritt)" className="field half" note="Bare hvis giveren vil ha kvittering med navn.">{(id, d) => <><input id={id} aria-describedby={d}
          maxLength={CashGifts.MAX_NAME} autoComplete="off" value={f.donorName} onChange={(e) => set({ ...f, donorName: e.target.value })} />{err('donorName')}</>}</Field>
        <Field label="E-post eller telefon (valgfritt)" className="field half">{(id) => <><input id={id} maxLength={CashGifts.MAX_CONTACT} autoComplete="off"
          value={f.donorContact} onChange={(e) => set({ ...f, donorContact: e.target.value })} />{err('donorContact')}</>}</Field>
      </div>
      {/* The person in the register (2026-10-31): optional; only for staff who may search the register. Shown on the
          person's «Gaver» tab, never in this list. */}
      {can('members.link') && <div className="row">
        {f.memberId
          ? <p className="note" style={{ margin: 0 }}>Medlem: <b>{f.memberLabel}</b> <Btn ghost onClick={() => set({ ...f, memberId: '', memberLabel: '' })}>Fjern</Btn>{err('memberId')}</p>
          : <div className="field"><PersonSearch label="Medlem (valgfritt) — søk" onPick={(m) => set({ ...f, memberId: String(m.id), memberLabel: `${m.name} · medl. ${m.memberNo || m.solidusNo}` })} />{err('memberId')}</div>}
      </div>}
      <div className="row">
        <Field label="Merknad" className="field">{(id) => <><textarea id={id} rows={2} maxLength={CashGifts.MAX_NOTE} value={f.note}
          onChange={(e) => set({ ...f, note: e.target.value })} />{err('note')}</>}</Field>
      </div>
      <p className="note">En annen enn deg må bekrefte gaven før kvitteringen får nummer. {CashGifts.TAX_NOTE}</p>
    </>
  );
}

/** One entry: the facts, the giver, the trail, and the moves this person may make. */
function Detail({ id, onChanged, onClose, onOpen }: { id: string; onChanged: () => void; onClose: () => void; onOpen: (id: string) => void }) {
  const { data, error, reload } = useLoad<Body>(`/cash-gifts/${id}`, [id]);
  const [msg, say] = useMsg();
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [going, setGoing] = useState(false);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const g: Body = data.gift;
  const conf: Body = g.confirmations || {};
  const confirmVipps = async () => {
    if (going) return;
    setGoing(true);
    const e = await startConfirm('cash_gift', g.id, 'confirmer');
    if (e) { say(e, 'err'); setGoing(false); }
  };
  const done = (r: { ok: boolean; body: Body }, okText: string) => { say(r.ok ? okText : r.body.message || errText(r.body), r.ok ? 'ok' : 'err'); if (r.ok) { reload(); onChanged(); } return r; };
  const receipt = async () => { const d = await download(`/v1/admin/cash-gifts/${g.id}/receipt.pdf`, `kvittering-${g.receiptNo}.pdf`); if (!d.ok) say(d.message || 'Kunne ikke hente kvitteringen.', 'err'); };
  return (
    <CardBody>
      <div className="work-bar"><b>{CashGifts.KIND_NO[g.kind]} {g.receiptNo || ''}</b> {stateTag(g.status)}<span className="sp" /><Btn ghost onClick={onClose}>Lukk</Btn></div>
      <table className="t">
        <tbody>
          <tr><th scope="row">Beløp</th><td>{krText(g.amountOre)}</td></tr>
          <tr><th scope="row">Dato mottatt</th><td>{longDate(g.receivedOn)}{g.locked ? ' (måneden er låst)' : ''}</td></tr>
          <tr><th scope="row">Formål</th><td>{PURPOSE[g.purpose] || g.purpose}</td></tr>
          {g.campaignId && <tr><th scope="row">Kampanje</th><td>{g.campaignId}</td></tr>}
          {g.original && <tr><th scope="row">Korrigerer</th><td><button type="button" className="linkish" onClick={() => onOpen(g.original.id)}>Kvittering {g.original.receiptNo || '—'}</button></td></tr>}
          {g.reversal && <tr><th scope="row">Korrigert</th><td><button type="button" className="linkish" onClick={() => onOpen(g.reversal.id)}>{CashGifts.STATE_NO[g.reversal.status]} korrigering ({g.reversal.receivedOn})</button></td></tr>}
          <tr><th scope="row">Giver</th><td>{g.donorName || 'Anonym'}{g.donorContact ? ` · ${g.donorContact}` : ''}</td></tr>
          {g.member && <tr><th scope="row">Medlem</th><td><a href={personHref(g.member.id)}>medl. {g.member.memberNo}</a></td></tr>}
          {g.note && <tr><th scope="row">{g.kind === 'reversal' ? 'Begrunnelse' : 'Merknad'}</th><td style={{ whiteSpace: 'pre-line' }}>{g.note}</td></tr>}
          <tr><th scope="row">Registrert av</th><td>{g.recordedByName || '—'} · {osloTime(g.recordedAt)}</td></tr>
          {g.decidedByName && <tr><th scope="row">{g.status === 'rejected' ? 'Avvist av' : 'Bekreftet av'}</th><td>{g.decidedByName} · {osloTime(g.decidedAt)}{g.decisionNote ? ` — ${g.decisionNote}` : ''}</td></tr>}
          {(conf.confirmer || g.can.confirmWithVipps) && <Stamp label="Gaven bekreftet" s={conf.confirmer || null} approver changedText="Bekreftelsen gjelder ikke lenger — registreringen er ikke den samme." />}
        </tbody>
      </table>
      <VoidedStamps voided={conf.voided} changedWord="endret" />
      {g.confirmationRequired && g.status === 'recorded' && <p className="note">Moskeen krever bekreftelse med Vipps: gaven bekreftes med «Bekreft gaven med Vipps».</p>}
      {g.receiptPath && <div className="row"><Btn ghost onClick={receipt}>Last ned kvittering (PDF)</Btn></div>}
      {(g.can.confirm || g.can.confirmWithVipps) && (
        <div className="row">
          {g.can.confirmWithVipps && <Btn small={false} disabled={going} onClick={confirmVipps}>{going ? 'Åpner Vipps …' : 'Bekreft gaven med Vipps'}</Btn>}
          {g.can.confirm && <TwoStep label="Bekreft" ghost={Boolean(g.can.confirmWithVipps)} confirmLabel="Klikk igjen: beløpet er talt og stemmer" busyLabel="Bekrefter …"
            onConfirm={async () => { done(await post(`/cash-gifts/${g.id}/confirm`, {}), 'Gaven er bekreftet.'); }} />}
          {g.can.confirmWithVipps && <span className="note"> Bekreft at du har talt beløpet. Gaven får kvitteringsnummer når Vipps har bekreftet deg.</span>}
        </div>
      )}
      {g.status === 'recorded' && !g.can.confirm && g.mine && <p className="note">Du registrerte denne. En annen må bekrefte den.</p>}
      {g.can.reject && (
        <div className="row">
          <Field label="Begrunnelse" className="field" note={g.mine ? 'Valgfritt når du avviser din egen registrering.' : 'Må fylles ut.'}>{(fid, d) => <textarea id={fid} aria-describedby={d} rows={2}
            maxLength={CashGifts.MAX_NOTE} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
          <TwoStep label="Avvis" confirmLabel="Klikk igjen for å avvise" busyLabel="Avviser …"
            onConfirm={async () => { done(await post(`/cash-gifts/${g.id}/reject`, { note: note.trim() || undefined }), 'Registreringen er avvist.'); }} />
        </div>
      )}
      {g.can.reverse && (
        <div className="row">
          <Field label="Hvorfor skal gaven korrigeres?" className="field" note="Gaven endres aldri. En korrigering med motsatt beløp registreres i dag, og en annen må bekrefte den.">{(fid, d) =>
            <textarea id={fid} aria-describedby={d} rows={2} maxLength={CashGifts.MAX_NOTE} value={reason} onChange={(e) => setReason(e.target.value)} />}</Field>
          <TwoStep label="Registrer korrigering" confirmLabel="Klikk igjen for å korrigere" busyLabel="Lagrer …"
            onConfirm={async () => { const r = done(await post(`/cash-gifts/${g.id}/reverse`, { reason: reason.trim() || undefined }), 'Korrigeringen er registrert og venter på bekreftelse.');
              if (r.ok && r.body.gift) onOpen(r.body.gift.id); }} />
        </div>
      )}
      <Msg text={msg.text} tone={msg.tone} />
      {(g.events || []).length > 0 && <>
        <h4>Historikk</h4>
        <ul className="note">{g.events.map((e: Body, i: number) => (
          <li key={i}>{osloTime(e.at)} · {e.actorName || '—'}: {e.from ? `${CashGifts.STATE_NO[e.from]} → ` : ''}{CashGifts.STATE_NO[e.to]}</li>))}</ul>
      </>}
    </CardBody>
  );
}

/** The locked months, each with who locked it and its «Lås måneden og bekreft med Vipps» confirmation, if any. */
function LockList({ seen }: { seen: number }) {
  const { data, error, reload } = useLoad<Body>('/cash-gifts/locks', [seen]);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  return (
    <table className="t">
      <tbody>{(data.locks || []).map((l: Body) => (
        <React.Fragment key={l.month}>
          <tr><th scope="row">{l.month}</th><td>Låst av {l.lockedByName || '—'} · {osloTime(l.lockedAt)}</td></tr>
          {l.confirmation && <Stamp label={`${l.month}: låsen bekreftet`} s={l.confirmation} approver changedText="Bekreftelsen gjelder ikke lenger — måneden er ikke den samme." />}
        </React.Fragment>))}</tbody>
    </table>
  );
}

export function Kontantgaver() {
  const [month, setMonth] = useState('');
  const [purpose, setPurpose] = useState('');
  const [state, setState] = useState('all');
  const qs = new URLSearchParams({ ...(month ? { month } : {}), ...(purpose ? { purpose } : {}), state }).toString();
  const path = `/cash-gifts?${qs}`;
  const { data, error, reload } = useLoad<Body>(path, [path]);
  const [open, setOpen] = useState<string | null>(null);
  const [msg, say] = useMsg();
  const [form, setForm] = useState<{ f: Form } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [lockMonth, setLockMonth] = useState('');
  const [withNames, setWithNames] = useState(false);
  const today = (data && data.today) || isoToday();
  const may: Body = (data && data.can) || {};
  const campaigns = useCampaignOptions(Boolean(data));
  const [locksSeen, setLocksSeen] = useState(0);
  const [lockGoing, setLockGoing] = useState(false);

  // Back from Vipps (components/VippsConfirm): a gift confirmed opens; a month locked shows in the list of locks.
  useVippsReturn('/kontantgaver', say, (b) => {
    if (b.subjectType === 'cash_gift_period_lock') say(`${b.subjectId} er låst og bekreftet med Vipps.`, 'ok');
    else { say('Gaven er bekreftet med Vipps.', 'ok'); setOpen(b.subjectId); }
    reload(); setLocksSeen((x) => x + 1);
  });
  const lockVipps = async () => {
    if (!CashGifts.validMonth(lockMonth)) return say('Velg en måned.', 'err');
    if (lockGoing) return;
    setLockGoing(true);
    const e = await startConfirm('cash_gift_period_lock', lockMonth, 'locker');
    if (e) { say(e, 'err'); setLockGoing(false); }
  };
  const campaignTitle = (cid: string) => (campaigns && campaigns.find((c) => c.id === cid)?.title) || cid;

  const save = async () => {
    if (!form || saving) return;
    const amount = Expenses.kroner(form.f.amount);
    if (!amount.ok) { setErrors({ amountOre: amount.message }); return; }
    setSaving(true);
    try {
      const f = form.f;
      const body = { amountOre: amount.ore, receivedOn: f.receivedOn, purpose: f.purpose, campaignId: f.campaignId.trim() || null,
        donorName: f.donorName.trim() || null, donorContact: f.donorContact.trim() || null, note: f.note.trim() || null,
        ...(f.memberId ? { memberId: f.memberId } : {}) };
      const r = await post('/cash-gifts', body);
      if (!r.ok) { setErrors(fieldErrors(r.body)); return say(errSummary(r.body), 'err'); }
      setForm(null); say('Gaven er registrert. En annen må bekrefte den.', 'ok'); reload();
      if (r.body.gift) setOpen(r.body.gift.id);
    } finally { setSaving(false); }
  };
  const lock = async () => {
    if (!CashGifts.validMonth(lockMonth)) return say('Velg en måned.', 'err');
    const r = await post('/cash-gifts/locks', { month: lockMonth });
    say(r.ok ? `${lockMonth} er låst.` : r.body.message || errText(r.body), r.ok ? 'ok' : 'err');
    if (r.ok) { setLockMonth(''); reload(); setLocksSeen((x) => x + 1); }
  };
  const exportCsv = async () => {
    const d = await download(`/v1/admin/cash-gifts/export.csv${withNames && may.lock ? '?donorNames=1' : ''}`, `kontantgaver-${today}.csv`);
    if (!d.ok) say(d.message || 'Kunne ikke hente CSV-filen.', 'err');
  };

  const items: Body[] = (data && data.items) || [];
  const totals: Body[] = (data && data.totals) || [];
  const locks: string[] = (data && data.locks) || [];
  return (
    <Card title="Kontantgaver" hint="Kontanter moskeen har fått. Én registrerer, en annen teller etter og bekrefter; da får kvitteringen nummer. Feil rettes med en korrigering, aldri ved å endre gaven.">
      <div className="work-bar">
        <label className="field inline">Måned <input type="month" value={month} max={today.slice(0, 7)} onChange={(e) => setMonth(e.target.value)} /></label>
        <label className="field inline">Formål <select value={purpose} onChange={(e) => setPurpose(e.target.value)}>
          <option value="">Alle</option>{CashGifts.PURPOSES.map((p) => <option key={p} value={p}>{PURPOSE[p]}</option>)}</select></label>
        <label className="field inline">Status <select value={state} onChange={(e) => setState(e.target.value)}>
          <option value="all">Alle</option>{CashGifts.STATES.map((s) => <option key={s} value={s}>{CashGifts.STATE_NO[s]}</option>)}</select></label>
        <span className="sp" />
        {may.record && <Btn small={false} onClick={() => { setErrors({}); say(''); setForm({ f: { ...EMPTY, receivedOn: today } }); }}>+ Registrer kontantgave</Btn>}
      </div>
      {!form && <CardBody><Msg text={msg.text} tone={msg.tone} /></CardBody>}
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <>
          <p className="vh" role="status">{items.length === 1 ? '1 registrering' : `${items.length} registreringer`}</p>
          <table className="t">
            <thead><tr><th scope="col">Dato</th><th scope="col">Kvittering</th><th scope="col">Type</th><th scope="col">Formål</th><th scope="col">Beløp</th>
              <th scope="col">Status</th><th scope="col">Registrert av</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
            <tbody>{items.length ? items.map((g) => (
              <tr key={g.id}>
                <td>{g.receivedOn}</td>
                <td>{g.receiptNo || '—'}</td>
                <td>{CashGifts.KIND_NO[g.kind] || g.kind}</td>
                <td>{PURPOSE[g.purpose] || g.purpose}{g.campaignId ? <div className="sub">{campaignTitle(g.campaignId)}</div> : null}</td>
                <td>{krText(g.amountOre)}</td>
                <td>{stateTag(g.status)}{g.confirmedWithVipps && <> <Tag tone="green">Bekreftet med Vipps</Tag></>}</td>
                <td>{g.recordedByName || '—'}</td>
                <td><Btn ghost aria-label={`Åpne registreringen fra ${g.receivedOn}`} onClick={() => setOpen(open === g.id ? null : g.id)}>{open === g.id ? 'Skjul' : 'Åpne'}</Btn></td>
              </tr>)) : <tr><td colSpan={8}>Ingen kontantgaver her ennå.</td></tr>}</tbody>
          </table>
          {open && <Detail id={open} onChanged={reload} onClose={() => setOpen(null)} onOpen={setOpen} />}
          <CardBody>
            <h4>Sum per måned og formål (bekreftet, med korrigeringer)</h4>
            {totals.length ? (
              <table className="t">
                <thead><tr><th scope="col">Måned</th><th scope="col">Formål</th><th scope="col">Gaver</th><th scope="col">Korrigeringer</th><th scope="col">Sum</th></tr></thead>
                <tbody>{totals.map((t) => <tr key={`${t.month}-${t.purpose}`}><td>{t.month}</td><td>{PURPOSE[t.purpose] || t.purpose}</td><td>{t.gifts}</td><td>{t.reversals}</td><td>{krText(t.ore)}</td></tr>)}</tbody>
              </table>
            ) : <p className="note">Ingen bekreftede gaver i utvalget.</p>}
            <h4>Til regnskapsføreren</h4>
            <div className="row">
              {may.lock && <label className="check"><input type="checkbox" checked={withNames} onChange={(e) => setWithNames(e.target.checked)} /> Ta med giverens navn (logges)</label>}
              <Btn ghost onClick={exportCsv}>Last ned CSV (i år)</Btn>
            </div>
            <h4>Låste måneder</h4>
            {locks.length ? <LockList seen={locksSeen} /> : <p className="note">Ingen måned er låst.</p>}
            {(may.lock || may.lockWithVipps) && (
              <div className="row">
                <Field label="Lås måned" className="field half" note="Når en måned er låst, kan ingenting med dato i den registreres eller bekreftes. Låsen kan ikke oppheves.">{(fid, d) =>
                  <input id={fid} aria-describedby={d} type="month" max={today.slice(0, 7)} value={lockMonth} onChange={(e) => setLockMonth(e.target.value)} />}</Field>
                {may.lockWithVipps && <Btn small={false} disabled={!lockMonth || lockGoing} onClick={lockVipps}>{lockGoing ? 'Åpner Vipps …' : 'Lås måneden og bekreft med Vipps'}</Btn>}
                {may.lock && <TwoStep label="Lås" confirmLabel="Klikk igjen for å låse for godt" busyLabel="Låser …" disabled={!lockMonth} onConfirm={lock} />}
              </div>
            )}
            {data.confirmationRequired && <p className="note">Moskeen krever bekreftelse med Vipps: gaver bekreftes med «Bekreft gaven med Vipps», og måneder låses med «Lås måneden og bekreft med Vipps».</p>}
          </CardBody>
        </>
      )}
      <FormDialog open={Boolean(form)} title="Registrer kontantgave" dirty={Boolean(form) && JSON.stringify(form!.f) !== JSON.stringify({ ...EMPTY, receivedOn: today })}
        onClose={() => setForm(null)}>
        {form && <>
          <GiftForm f={form.f} set={(f) => setForm({ f })} errors={errors} today={today} campaigns={campaigns} />
          <div className="row"><Btn small={false} disabled={saving} onClick={save}>{saving ? 'Lagrer …' : 'Registrer'}</Btn></div>
          <Msg text={msg.text} tone={msg.tone} />
        </>}
      </FormDialog>
    </Card>
  );
}
